import React, { useRef, useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import Confetti from 'react-confetti';
import { FaEdit, FaWhatsapp, FaInstagram, FaMicrophone } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import AudioRecorder from './AudioRecorder';

// Create an axios instance with custom config
const isDevelopment = process.env.NODE_ENV === 'development';

const axiosInstance = axios.create({
  baseURL: isDevelopment ? 'http://localhost:3001' : window.location.origin, // Use localhost in development, current origin in production
  timeout: 85000, // 85 seconds - slightly less than Heroku's 90s limit
  headers: {
    'Connection': 'keep-alive',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  }
});

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
const MAX_FILES_PER_UPLOAD = 6; // Maximum files per upload

// FileConfetti component to handle individual file success animations
const FileConfetti = ({ trigger, windowDimensions }) => {
  const [showBurst, setShowBurst] = useState(false);
  const [lastTrigger, setLastTrigger] = useState(0);

  useEffect(() => {
    
    if (trigger > lastTrigger) {
      setShowBurst(true);
      setLastTrigger(trigger);
      
      const timer = setTimeout(() => {
        setShowBurst(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [trigger, lastTrigger]);

  if (!showBurst) return null;

  return (
    <Confetti
      width={windowDimensions.width}
      height={windowDimensions.height}
      recycle={false}
      numberOfPieces={80}
      gravity={0.4}
      confettiSource={{
        x: 0,
        y: -50,
        w: windowDimensions.width,
        h: 50
      }}
      initialVelocityY={3}
      initialVelocityX={5}
      wind={0.02}
      colors={['#3b82f6', '#8b5cf6', '#ec4899', '#10b981', '#f59e0b', '#ef4444']}
    />
  );
};

// Mock couple photo
const DEFAULT_COUPLE_PHOTO = "";

function UploadNotePage() {
  const { uploadPath } = useParams();
  
  // Special font for specific wedding (new format: brideName-groomName)
  const specialFontHashes = ['ayşegül-aytuğ'];
  const useGilroy = specialFontHashes.includes(uploadPath);
  
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [allowedPaths, setAllowedPaths] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [fileConfettiTrigger, setFileConfettiTrigger] = useState(0);
  const [weddingInfo, setWeddingInfo] = useState(null);
  const [uploadsEnabled, setUploadsEnabled] = useState(true);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [nameText, setNameText] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [couplePhoto, setCouplePhoto] = useState(DEFAULT_COUPLE_PHOTO); // Dynamic background state
  const [showAudioRecorder, setShowAudioRecorder] = useState(false); // Audio recorder modal state
  const fileInputRef = useRef(null);
  const noteFileInputRef = useRef(null);
  const keepAliveIntervalRef = useRef(null);
  const { t } = useTranslation();

  // Single-source disabled state: disable picker while there are files pending upload
  const hasPendingSelection = selectedFiles.some(file => !file.uploaded);


  
  React.useEffect(() => {
    if (uploadPath) {
      // Check if wedding has background first
      checkWeddingBackground(uploadPath).then(hasBackground => {
        if (hasBackground) {
          // Use secure backend proxy endpoint with cache busting - ABSOLUTE URL for CSS background-image
          const timestamp = Date.now();
          const absoluteUrl = `${axiosInstance.defaults.baseURL}/api/backgrounds/image/${uploadPath}?t=${timestamp}`;
          setCouplePhoto(absoluteUrl);
        } else {
          // No custom background, use default
          setCouplePhoto(DEFAULT_COUPLE_PHOTO);
        }
      }).catch(error => {
        console.error(`Error checking background:`, error);
        // Fallback to default
        setCouplePhoto(DEFAULT_COUPLE_PHOTO);
      });
    }
  }, [uploadPath]);

  // Function to check if wedding background exists
  const checkWeddingBackground = async (weddingPath) => {
    try {
      const timestamp = Date.now();
      const url = `/api/backgrounds/path/${weddingPath}?t=${timestamp}`;
      const response = await axiosInstance.get(url);
      
      if (response.data.success && response.data.data) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error(`Error checking background:`, error.response?.data || error.message);
      return false;
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // File reader for previews
  const createFilePreview = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(file);
    });
  };

  // Validate path and fetch wedding info on component mount
  useEffect(() => {
    const validatePathAndFetchWeddingInfo = async () => {
      try {
        if (!uploadPath) {
          setUploadError('Geçersiz düğün yolu.');
          setIsLoading(false);
          return;
        }

        // Validate path first
        const validationResponse = await axiosInstance.get(`/api/validate-path/${uploadPath}`);
        
        if (!validationResponse.data.success || !validationResponse.data.isValid) {
          setUploadError('Bu düğün yolu bulunamadı.');
          setIsLoading(false);
          return;
        }

        // Path is valid, now fetch wedding info
        try {
          const weddingResponse = await axiosInstance.get(`/api/wedding-hash/${uploadPath}`);
          if (weddingResponse.data.success) {
            setWeddingInfo(weddingResponse.data);
            setUploadsEnabled(weddingResponse.data.isEnabled);
            
            // Set allowedPaths to just this validated path
            setAllowedPaths([uploadPath]);
          } else {
            setUploadError('Düğün bilgileri alınamadı.');
          }
        } catch (weddingError) {
          console.error('Error fetching wedding info:', weddingError);
          setUploadError('Düğün bilgileri alınırken hata oluştu.');
        }
      } catch (error) {
        console.error('Error validating path:', error);
        setUploadError('Yol doğrulaması yapılırken hata oluştu.');
      } finally {
        setIsLoading(false);
      }
    };

    validatePathAndFetchWeddingInfo();
  }, [uploadPath]);



  // Clean up interval on component unmount
  useEffect(() => {
    return () => {
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
      }
    };
  }, []);

    // Show loading state
  if (isLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat relative"
        style={{ backgroundImage: `url(${couplePhoto})` }}
      >
        {/* Subtle overlay for better spinner visibility */}
        <div className="absolute inset-0 bg-black/20"></div>
        
        {/* Loading Content */}
        <div className="relative z-10 flex flex-col items-center gap-6">
          {/* Elegant Loading Spinner */}
          <div className="relative">
            <div className="animate-spin rounded-full h-32 w-32 border-4 border-white/20"></div>
            <div className="animate-spin rounded-full h-32 w-32 border-4 border-t-white border-r-white/60 border-b-white/30 border-l-white/60 absolute top-0"></div>
          </div>
          
          {/* Loading Text */}
          <div className="text-center">
            <h2 className="text-white text-xl font-semibold drop-shadow-lg mb-2">
              {t('admin.loading')}
            </h2>
            <p className="text-white/80 text-sm drop-shadow-md">
              ✨ Anılarınız hazırlanıyor...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Verify the upload path matches one of the allowed paths
  if (!allowedPaths.includes(uploadPath)) {
    return <Navigate to="/" replace />;
  }

  const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

  // Function to parse multiple names based on capital letters
  const parseNames = (nameString) => {
    if (!nameString) return '';
    
    // Split the string at capital letters (except the first character)
    const names = nameString.split(/(?=[A-Z])/).filter(name => name.length > 0);
    
    // Capitalize each name
    const capitalizedNames = names.map(name => capitalize(name.toLowerCase()));
    
    return capitalizedNames.join(' ');
  };

  // Get wedding couple names from the path (new format: brideName-groomName)
  const [brideName, groomName] = uploadPath.split('-');
  
  // Use current date for display
  const now = new Date();
  const formattedDate = `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
  
  // Parse multiple names for display
  const parsedBrideName = parseNames(brideName);
  const parsedGroomName = parseNames(groomName);

  // Function to send keep-alive request
  const sendKeepAlive = async () => {
    try {
      await axiosInstance.get('/api/media/keep-alive', { timeout: 5000 });
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Keep-alive ping failed:`, error);
      setUploadError(t('uploadPage.connectionError'));
    }
  };

  const uploadChunk = async (file, start, end, attempt = 1) => {
    const chunk = file.slice(start, end);
    const chunkForm = new FormData();
    chunkForm.append('chunk', chunk);
    chunkForm.append('fileName', file.name);
    chunkForm.append('start', start);
    chunkForm.append('end', end);
    chunkForm.append('total', file.size);
    chunkForm.append('mimeType', file.type);

    try {
      const response = await axiosInstance.post(`/api/media/${uploadPath}/upload-chunk`, chunkForm, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          const overallProgress = Math.round(((start + (progressEvent.loaded * (end - start) / progressEvent.total)) * 100) / file.size);
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: overallProgress
          }));
        }
      });
      return response.data;
    } catch (error) {
      if (attempt < 3) {
        return uploadChunk(file, start, end, attempt + 1);
      }
      throw error;
    }
  };

  const uploadLargeFile = async (file, note = null) => {
    const chunks = Math.ceil(file.size / CHUNK_SIZE);

    try {
      for (let i = 0; i < chunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        await uploadChunk(file, start, end);
      }

      // Final completion request
      const requestData = {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type
      };
      
      if (note) {
        requestData.note = note;
      }
      
      const response = await axiosInstance.post(`/api/media/${uploadPath}/complete-upload`, requestData);

      return response.data;
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Error in chunked upload for ${file.name}:`, error);
      throw error;
    }
  };

  const handleNoteFileChange = (e) => {
    // Check if uploads are enabled before proceeding
    if (!uploadsEnabled) {
      alert('Gelin ve damat şu anda yükleme özelliğini aktif etmedi');
      e.target.value = null; // Clear the file input
      return;
    }

    const rawFiles = Array.from(e.target.files || []);
    const files = rawFiles.filter(f => f && typeof f.size === 'number' && f.size > 0);
    if (files.length === 0) return;

    // Check max file limit
    if (files.length > MAX_FILES_PER_UPLOAD) {
      alert(t('uploadPage.maxFilesError', { max: MAX_FILES_PER_UPLOAD }));
      e.target.value = null;
      return;
    }
    
    setPendingFiles(files);
    setShowNoteModal(true);
  };

  const handleUploadWithNote = async () => {
    if (pendingFiles.length === 0) return;
    
    setShowNoteModal(false);
    
    // Combine name and note with pipe delimiter
    const combinedNote = nameText.trim() && noteText.trim() 
      ? `${nameText.trim()}|${noteText.trim()}`
      : nameText.trim() || noteText.trim() || null;
    
    // Create file previews with note
    const filePreviewPromises = pendingFiles.map(async (file) => {
      const preview = file.type.startsWith('image/') 
        ? await createFilePreview(file) 
        : null;
      
      return {
        name: file.name,
        size: file.size,
        type: file.type,
        preview,
        uploaded: false,
        storageUrl: null,
        note: combinedNote, // Add combined note to file data
      };
    });
    
    const filePreviews = await Promise.all(filePreviewPromises);
    setSelectedFiles(prev => [...prev, ...filePreviews]);
    
    // Process the upload with combined note
    await processUpload(pendingFiles, combinedNote);
    
    // Reset states
    setPendingFiles([]);
    setNoteText('');
    setNameText('');
  };

  const handleCancelNote = () => {
    setShowNoteModal(false);
    setPendingFiles([]);
    setNoteText('');
    setNameText('');
    if (noteFileInputRef.current) {
      noteFileInputRef.current.value = null;
    }
  };

  // Handler for audio recording save
  const handleAudioSave = async (audioFile, note) => {
    // Check if uploads are enabled
    if (!uploadsEnabled) {
      alert('Gelin ve damat şu anda yükleme özelliğini aktif etmedi');
      return;
    }

    // Add audio file to selected files for preview
    setSelectedFiles(prev => [...prev, {
      name: audioFile.name,
      size: audioFile.size,
      type: audioFile.type,
      preview: null, // Audio doesn't have visual preview
      uploaded: false,
      storageUrl: null,
      note: note,
      isAudio: true, // Flag to identify audio files
    }]);

    // Process upload
    await processUpload([audioFile], note);
  };

  const processUpload = async (files, note = null) => {
    setUploading(true);
    setUploadError(null);
    setUploadProgress(prev => {
        const newProgress = { ...prev };
        files.forEach(file => {
            newProgress[file.name] = 0;
        });
        return newProgress;
    });

    const successfulUploads = [];
    const failedUploads = [];
    let successfulUploadCount = 0;

    // Start keep-alive interval
    keepAliveIntervalRef.current = setInterval(sendKeepAlive, 30000);

    for (const file of files) {
      try {
        let response;
        // Use chunked upload for large files OR audio files (for transcoding)
        const isAudioFile = file.type && file.type.startsWith('audio/');
        if (file.size > CHUNK_SIZE || isAudioFile) {
          response = await uploadLargeFile(file, note);
        } else {
          const formData = new FormData();
          formData.append('media', file);
          if (note) {
            formData.append('note', note);
          }
          
          const uploadResponse = await axiosInstance.post(`/api/media/${uploadPath}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(prev => ({ ...prev, [file.name]: percentCompleted }));
            }
          });
          response = uploadResponse.data;
        }

        if (response.success && response.data) {
          successfulUploadCount++;
          
          setSelectedFiles(prevFiles =>
            prevFiles.map(prevFile => {
              if (prevFile.name === file.name && !prevFile.uploaded) {
                return {
                  ...prevFile,
                  uploaded: true,
                  storageUrl: response.data.storage_url,
                };
              }
              return prevFile;
            })
          );

          setFileConfettiTrigger(prev => prev + 1);

        } else {
          failedUploads.push(file.name);
        }
      } catch (error) {
        console.error(`[${new Date().toISOString()}] Error uploading ${file.name}:`, error.response || error);
        let errorMessage = `${file.name}: `;
        if (error.code === 'ECONNABORTED') {
          errorMessage += 'Bağlantı zaman aşımına uğradı';
        } else if (error.response?.status === 413) {
          errorMessage += 'Dosya boyutu çok büyük';
        } else if (error.response?.data?.message) {
          errorMessage += error.response.data.message;
        } else {
          errorMessage += 'Yükleme başarısız oldu';
        }
        failedUploads.push(errorMessage);
        setUploadError(`Yükleme sırasında hata oluştu: ${error.response?.data?.message || error.message}`);
      }
    }

    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    
    if (failedUploads.length > 0) {
      const failedNames = failedUploads.join('\n');
      setUploadError(`Bazı dosyalar yüklenemedi:\n${failedNames}\nLütfen tekrar deneyin veya dosya boyutunu kontrol edin.`);
    }
    
    if (successfulUploadCount + failedUploads.length === files.length) {
        setUploading(false);
        
        // Show big confetti celebration when all uploads are complete and successful
        if (successfulUploadCount > 0 && failedUploads.length === 0) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 5000); // Big celebration for 5 seconds
        }
    }
  };

  const handleFileChange = async (e) => {
    // Check if uploads are enabled before proceeding
    if (!uploadsEnabled) {
      alert('Gelin ve damat şu anda yükleme özelliğini aktif etmedi');
      e.target.value = null; // Clear the file input
      return;
    }

    const rawFiles = Array.from(e.target.files || []);
    const files = rawFiles.filter(f => f && typeof f.size === 'number' && f.size > 0);
    if (files.length === 0) return;

    // Check max file limit
    if (files.length > MAX_FILES_PER_UPLOAD) {
      alert(t('uploadPage.maxFilesError', { max: MAX_FILES_PER_UPLOAD }));
      e.target.value = null;
      return;
    }

    // Create previews for selected files and add to state immediately
    const filePreviewPromises = files.map(async (file) => {
      const preview = file.type.startsWith('image/') 
        ? await createFilePreview(file) 
        : null;
      
      return {
        name: file.name,
        size: file.size,
        type: file.type,
        preview,
        uploaded: false,
        storageUrl: null,
        note: null, // No note for regular upload
      };
    });
    
    const filePreviews = await Promise.all(filePreviewPromises);
    setSelectedFiles(prev => [...prev, ...filePreviews]);
    
    // Process upload without note
    await processUpload(files, null);
    
    e.target.value = null;
  };

  return (
    <div 
      className={`min-h-screen bg-cover bg-center bg-no-repeat relative overflow-hidden ${useGilroy ? 'gilroy-font' : ''}`}
      style={{ backgroundImage: `url(${couplePhoto})` }}
    >
      {/* Big confetti for complete upload */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }}>
          <Confetti
            width={windowDimensions.width}
            height={windowDimensions.height}
            recycle={false}
            numberOfPieces={200}
            gravity={0.3}
          />
        </div>
      )}

      {/* Small confetti burst for each successful file upload */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }}>
        <FileConfetti 
          trigger={fileConfettiTrigger} 
          windowDimensions={windowDimensions} 
        />
      </div>

      {/* Dark overlay to make text readable */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col">

        {/* Language Switcher */}
        <div className="absolute top-4 right-4 z-20">
          <LanguageSwitcher />
        </div>

        {/* Upper Content - Moved texts up */}
        <div className="px-4 pt-8">
          
          {/* Main Title */}
          <div className="text-center mb-8">
            <div className="text-white/70 text-sm mb-3 drop-shadow-sm">
              {formattedDate}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow-lg">
              {parsedBrideName} & {parsedGroomName}
            </h1>
            <h2 className="text-xl md:text-2xl font-semibold text-white/95 mb-2 drop-shadow-md">
              {t('uploadPage.shareMemories')}
            </h2>
          </div>
        </div>



        {/* Error Message */}
        {uploadError && (
          <div className="mx-4 mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {uploadError.split('\n').map((line, i) => (
              <p key={i} className="text-sm">{line}</p>
            ))}
          </div>
        )}

        {/* Spacer for background photo visibility */}
        <div className="flex-1"></div>

        {/* File Previews */}
        {selectedFiles.length > 0 && (
          <div className="mx-4 mb-4">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20">
              <h3 className="text-white font-semibold mb-3">{t('uploadPage.uploadedFilesColon')}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {selectedFiles.map((fileData, index) => {
                  const progress = uploadProgress[fileData.name] || 0;
                  const isUploaded = fileData.uploaded;
                  
                  return (
                    <div key={index} className="bg-white/20 rounded-lg p-2 text-center relative">
                      <div className="aspect-square mb-2 bg-gray-200 rounded-lg overflow-hidden relative">
                        {fileData.type.startsWith('audio/') ? (
                          // Audio file thumbnail
                          <div className="flex items-center justify-center h-full bg-gradient-to-br from-orange-500 to-red-500">
                            <FaMicrophone className="h-12 w-12 text-white" />
                            {!isUploaded && (
                              <div className="absolute inset-0 flex items-center justify-center z-20">
                                <div className="relative">
                                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500/80 to-red-600/80 backdrop-blur-sm rounded-full animate-pulse"></div>
                                  <div className="relative bg-black/30 backdrop-blur-sm rounded-full p-3 border border-white/20 w-16 h-16 flex items-center justify-center">
                                    <svg width="40" height="40" viewBox="0 0 40 40" className="absolute">
                                      <circle cx="20" cy="20" r="18" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2"/>
                                      <circle 
                                        cx="20" cy="20" r="18" fill="none" 
                                        stroke="url(#audioProgressGradient)" strokeWidth="3" 
                                        strokeDasharray={`${progress * 0.01 * 2 * Math.PI * 18} ${2 * Math.PI * 18}`} 
                                        strokeLinecap="round" transform="rotate(-90 20 20)"
                                        className="transition-all duration-300 ease-out"
                                      />
                                      <defs>
                                        <linearGradient id="audioProgressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                          <stop offset="0%" style={{stopColor: '#fb923c', stopOpacity: 1}} />
                                          <stop offset="100%" style={{stopColor: '#ef4444', stopOpacity: 1}} />
                                        </linearGradient>
                                      </defs>
                                    </svg>
                                    <span className="text-white text-xs font-bold drop-shadow-lg relative z-10">{progress}%</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : fileData.type.startsWith('image/') ? (
                          <>
                            {fileData.preview && (
                              <img 
                                src={fileData.preview} 
                                alt={fileData.name} 
                                className="w-full h-full object-cover" 
                              />
                            )}
                            {!isUploaded && (
                              <div className="absolute inset-0 flex items-center justify-center z-20">
                                <div className="relative">
                                  {/* Modern gradient background with blur */}
                                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/80 to-purple-600/80 backdrop-blur-sm rounded-full animate-pulse"></div>
                                  
                                  {/* Progress circle with enhanced design */}
                                  <div className="relative bg-black/30 backdrop-blur-sm rounded-full p-3 border border-white/20">
                                    <svg width="40" height="40" viewBox="0 0 40 40" className="transform">
                                      {/* Background circle with gradient */}
                                      <circle 
                                        cx="20" 
                                        cy="20" 
                                        r="18" 
                                        fill="none" 
                                        stroke="rgba(255,255,255,0.2)" 
                                        strokeWidth="2"
                                      />
                                      
                                      {/* Progress circle with animated gradient */}
                                      <circle 
                                        cx="20" 
                                        cy="20" 
                                        r="18" 
                                        fill="none" 
                                        stroke="url(#progressGradient)" 
                                        strokeWidth="3" 
                                        strokeDasharray={`${progress * 0.01 * 2 * Math.PI * 18} ${2 * Math.PI * 18}`} 
                                        strokeLinecap="round" 
                                        transform="rotate(-90 20 20)"
                                        className="transition-all duration-300 ease-out"
                                      />
                                      
                                      {/* Gradient definition */}
                                      <defs>
                                        <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                          <stop offset="0%" style={{stopColor: '#60a5fa', stopOpacity: 1}} />
                                          <stop offset="50%" style={{stopColor: '#a78bfa', stopOpacity: 1}} />
                                          <stop offset="100%" style={{stopColor: '#f472b6', stopOpacity: 1}} />
                                        </linearGradient>
                                      </defs>
                                      
                                      {/* Percentage text with shadow */}
                                      <text 
                                        x="20" 
                                        y="20" 
                                        textAnchor="middle" 
                                        dominantBaseline="middle" 
                                        fill="white" 
                                        fontSize="9" 
                                        fontWeight="bold"
                                        className="drop-shadow-lg"
                                      >
                                        {progress}%
                                      </text>
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          // Video thumbnail with progress overlay
                          <div className="flex items-center justify-center h-full bg-gray-800">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {!isUploaded && (
                              <div className="absolute inset-0 flex items-center justify-center z-20">
                                <div className="relative">
                                  {/* Modern gradient background with blur */}
                                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/80 to-purple-600/80 backdrop-blur-sm rounded-full animate-pulse"></div>
                                  
                                  {/* Progress circle with enhanced design */}
                                  <div className="relative bg-black/30 backdrop-blur-sm rounded-full p-3 border border-white/20 w-16 h-16 flex items-center justify-center">
                                    <svg width="40" height="40" viewBox="0 0 40 40" className="absolute">
                                      {/* Background circle */}
                                      <circle 
                                        cx="20" 
                                        cy="20" 
                                        r="18" 
                                        fill="none" 
                                        stroke="rgba(255,255,255,0.2)" 
                                        strokeWidth="2"
                                      />
                                      
                                      {/* Progress circle with gradient */}
                                      <circle 
                                        cx="20" 
                                        cy="20" 
                                        r="18" 
                                        fill="none" 
                                        stroke="url(#videoProgressGradient)" 
                                        strokeWidth="3" 
                                        strokeDasharray={`${progress * 0.01 * 2 * Math.PI * 18} ${2 * Math.PI * 18}`} 
                                        strokeLinecap="round" 
                                        transform="rotate(-90 20 20)"
                                        className="transition-all duration-300 ease-out"
                                      />
                                      
                                      {/* Gradient definition for video */}
                                      <defs>
                                        <linearGradient id="videoProgressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                          <stop offset="0%" style={{stopColor: '#60a5fa', stopOpacity: 1}} />
                                          <stop offset="50%" style={{stopColor: '#a78bfa', stopOpacity: 1}} />
                                          <stop offset="100%" style={{stopColor: '#f472b6', stopOpacity: 1}} />
                                        </linearGradient>
                                      </defs>
                                    </svg>
                                    
                                    {/* Percentage text with shadow */}
                                    <span className="text-white text-xs font-bold drop-shadow-lg relative z-10">
                                      {progress}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Bottom Section - Moved Buttons Here */}
        <div className="pb-8 px-4">
          
          {/* Upload Options - Moved to Bottom */}
          <div className="space-y-4 max-w-72 mx-auto w-full mb-8">

            {/* Modern Story-style Upload Button with Progress */}
            {uploadsEnabled ? (
              <label 
                htmlFor="fileInputMain"
                className={`block w-full relative group overflow-hidden ${
                  uploading || hasPendingSelection ? 'scale-[0.98] opacity-50 pointer-events-none' : 'hover:scale-105'
                } transition-all duration-300`}
              >
                <div className={`backdrop-blur-md border rounded-full py-2 px-4 transition-all duration-300 relative ${
                  uploading 
                    ? 'bg-gradient-to-r from-indigo-500/80 to-purple-600/80 border-white/40 shadow-2xl' 
                    : 'bg-white/20 border-white/30 group-hover:bg-white/30 group-active:scale-95 shadow-xl'
                }`}>
                  
                  {/* Background Progress Bar */}
                  {uploading && Object.values(uploadProgress).length > 0 && (
                    <div 
                      className="absolute left-0 top-0 h-full bg-gradient-to-r from-white/20 to-white/30 transition-all duration-500 ease-out rounded-full"
                      style={{ 
                        width: `${Math.min(100, Object.values(uploadProgress).reduce((a, b) => a + b, 0) / Math.max(1, Object.values(uploadProgress).length))}%` 
                      }}
                    />
                  )}
                  
                  {/* Animated pulse effect when uploading */}
                  {uploading && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse rounded-full" />
                  )}
                  
                  <div className="flex items-center justify-center gap-3 relative z-10 min-h-[2.5rem]">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <div className="flex flex-col items-center justify-center">
                      <span className="text-white font-semibold text-base drop-shadow-sm">
                        {uploading ? t('uploadPage.uploading') : t('uploadPage.uploadPhotoVideo')}
                      </span>
                      {uploading && Object.values(uploadProgress).length > 0 && (
                        <span className="text-white/80 text-sm drop-shadow-sm">
                          {Math.round(Object.values(uploadProgress).reduce((a, b) => a + b, 0) / Math.max(1, Object.values(uploadProgress).length))}% - {Object.keys(uploadProgress).filter(key => uploadProgress[key] === 100).length} / {Object.keys(uploadProgress).length} files
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </label>
            ) : (
              <button type="button"
                className={`block w-full relative group overflow-hidden bg-gray-400/30 border border-gray-400/50 rounded-full py-2 px-4 transition-all duration-300 shadow-md opacity-100`}
                onClick={() => alert('Gelin ve damat şu anda yükleme özelliğini aktif etmedi')}
              >
                <div className="flex items-center justify-center gap-3 relative z-10 min-h-[2.5rem]">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <div className="flex flex-col items-center justify-center">
                    <span className="text-white font-semibold text-base drop-shadow-sm">
                      {t('uploadPage.uploadPhotoVideo')}
                    </span>
                  </div>
                </div>
              </button>
            )}

            {/* Modern Note Upload Button */}
            {uploadsEnabled ? (
              <label
                htmlFor="fileInputNote"
                className={`block w-full relative group overflow-hidden ${
                  uploading || hasPendingSelection ? 'opacity-50 pointer-events-none' : 'hover:scale-105'
                } transition-all duration-300`}
              >
                <div className={`backdrop-blur-md border rounded-full py-2 px-4 transition-all duration-300 shadow-xl relative ${
                  'bg-gradient-to-r from-pink-200/50 to-purple-200/50 border-pink-200/60 group-hover:from-pink-300/60 group-hover:to-purple-300/60 group-active:scale-95'
                }`}>
                  
                  {/* Animated gradient background on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-400/20 to-purple-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full" />
                  
                  <div className="flex items-center justify-center gap-3 relative z-10 min-h-[2.5rem]">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="text-white font-semibold text-base drop-shadow-sm">
                      {t('uploadPage.uploadWithMessage')}
                    </span>
                  </div>
                </div>
              </label>
            ) : (
              <button type="button"
                onClick={() => alert('Gelin ve damat şu anda yükleme özelliğini aktif etmedi')}
                className={`block w-full relative group overflow-hidden bg-gray-400/30 border border-gray-400/50 rounded-full py-2 px-4 transition-all duration-300 shadow-md`}
              >
                <div className="flex items-center justify-center gap-3 relative z-10 min-h-[2.5rem]">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="text-white font-semibold text-base drop-shadow-sm">
                    {t('uploadPage.uploadWithMessage')}
                  </span>
                </div>
              </button>
            )}

            {/* Voice Message Recording Button */}
            {uploadsEnabled ? (
              <button
                onClick={() => setShowAudioRecorder(true)}
                className={`block w-full relative group overflow-hidden ${
                  uploading || hasPendingSelection ? 'opacity-50 pointer-events-none' : 'hover:scale-105'
                } transition-all duration-300`}
              >
                <div className="backdrop-blur-md border rounded-full py-2 px-4 transition-all duration-300 shadow-xl relative bg-gradient-to-r from-orange-200/50 to-red-200/50 border-orange-200/60 group-hover:from-orange-300/60 group-hover:to-red-300/60 group-active:scale-95">
                  
                  {/* Animated gradient background on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-400/20 to-red-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full" />
                  
                  <div className="flex items-center justify-center gap-3 relative z-10 min-h-[2.5rem]">
                    <FaMicrophone className="w-5 h-5 text-white" />
                    <span className="text-white font-semibold text-base drop-shadow-sm">
                      {t('uploadPage.recordVoice')}
                    </span>
                  </div>
                </div>
              </button>
            ) : (
              <button type="button"
                onClick={() => alert('Gelin ve damat şu anda yükleme özelliğini aktif etmedi')}
                className="block w-full relative group overflow-hidden bg-gray-400/30 border border-gray-400/50 rounded-full py-2 px-4 transition-all duration-300 shadow-md"
              >
                <div className="flex items-center justify-center gap-3 relative z-10 min-h-[2.5rem]">
                  <FaMicrophone className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold text-base drop-shadow-sm">
                    {t('uploadPage.recordVoice')}
                  </span>
                </div>
              </button>
            )}
          </div>

          {/* Social Links */}
          <div className="flex justify-center gap-4">
            <a href="https://wa.me/905372243441" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110">
              <FaWhatsapp className="text-white text-xl" />
            </a>
            <a href="https://instagram.com/weddingram.me" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110">
              <FaInstagram className="text-white text-xl" />
            </a>
          </div>
        </div>
      </div>

      {/* File inputs (sr-only for better in-app browser compatibility) */}
      <input
        id="fileInputMain"
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="sr-only"
        onChange={handleFileChange}
      />
      
      <input
        id="fileInputNote"
        ref={noteFileInputRef}
        type="file"
        accept="image/*,video/*"
        className="sr-only"
        onChange={handleNoteFileChange}
      />

      {/* Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full mx-4 shadow-2xl">

            {/* Modal Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <FaEdit className="text-pink-600 text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">
                {t('uploadPage.addYourMessage')}
              </h3>
              <p className="text-gray-500">
                {t('uploadPage.howDidYouFeel')}
              </p>
            </div>

            <div className="space-y-5 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('uploadPage.tellUsWhoYouAre')}
                </label>
                <input
                  type="text"
                  value={nameText}
                  onChange={(e) => setNameText(e.target.value)}
                  placeholder={t('uploadPage.namePlaceholder')}
                  className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-pink-500 transition-colors text-gray-800 placeholder-gray-400"
                  maxLength={50}
                />
                <div className="text-right text-xs text-gray-400 mt-1">
                  {nameText.length}/50
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('uploadPage.writeYourMessage')}
                </label>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder={t('uploadPage.notePlaceholder')}
                  className="w-full h-32 p-4 border-2 border-gray-200 rounded-2xl resize-none focus:outline-none focus:border-pink-500 transition-colors text-gray-800 placeholder-gray-400"
                  maxLength={200}
                />
                <div className="text-right text-xs text-gray-400 mt-1">
                  {noteText.length}/200
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-4">
                <p className="text-sm text-gray-600 mb-2">
                  {t('uploadPage.selectedFile')}
                </p>
                <p className="text-sm font-medium text-gray-800">
                  {pendingFiles.length > 0 && pendingFiles[0].name}
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleCancelNote}
                className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-2xl transition-colors"
              >
                {t('uploadPage.cancel')}
              </button>
              <button
                onClick={handleUploadWithNote}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold rounded-2xl transition-all transform hover:scale-105"
              >
                {t('uploadPage.uploadWithNote')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audio Recorder Modal */}
      <AudioRecorder
        isOpen={showAudioRecorder}
        onClose={() => setShowAudioRecorder(false)}
        onSave={handleAudioSave}
        uploadsEnabled={uploadsEnabled}
      />
    </div>
  );
}

export default UploadNotePage; 